import { inject } from '@angular/core';
import { CanActivateFn, ActivatedRouteSnapshot, RouterStateSnapshot } from '@angular/router';
import { Observable, of, Subject } from 'rxjs';
import { map, tap } from 'rxjs/operators';
import { SnippetsService } from '../services/SnippetsService';
import {
  ILanguage,
  ILanguagesAndSnippets,
  ISnippet,
  LanguageStoreService,
  SnippetsStoreService
} from '@cinetixx/cinetixx-ui';
import { environment } from 'src/environments/environment';

export const languagesSnippets$ = new Subject<ILanguagesAndSnippets>();

export const languagesSnippetsGuard: CanActivateFn = (
  route: ActivatedRouteSnapshot,
  state: RouterStateSnapshot
): Observable<boolean> => {
  const snippetsService = inject(SnippetsService);
  const languageStoreService = inject(LanguageStoreService);
  const snippetsStoreService = inject(SnippetsStoreService);

  if (languageStoreService.language && snippetsStoreService.snippets.length) {
    return of(true);
  }

  const getAllLanguages$ = (): Observable<ILanguage[]> => {
    const { fallbackLanguage } = environment;

    return snippetsService.getAllLanguages().pipe(
      tap(languages => {
        const language = route.params['language'];
        let fLanguage = languages.find(x => x.token === language);

        if (!fLanguage && language !== fallbackLanguage) {
          fLanguage = languageStoreService.language ?? languages.find(x => x.token === fallbackLanguage);
          location.href = `/${ fLanguage.token }${ state.url }`;
        }

        languageStoreService.language = fLanguage;
      })
    );
  };

  const getAllSnippets$ = (): Observable<ISnippet[]> => {
    return snippetsService.getAllSnippets(languageStoreService.language.token).pipe(
      tap(snippets => snippetsStoreService.snippets = snippets)
    );
  };

  getAllLanguages$().subscribe(languages => {
    if (languageStoreService.language) {
      getAllSnippets$().subscribe(snippets => {
        languagesSnippets$.next({
          languages,
          snippets
        });
      });
    }
  });

  return languagesSnippets$.asObservable().pipe(map(() => true));
};
